import { useMemo, useState } from 'react';
import { AbsoluteFill, Sequence, useCurrentFrame } from 'remotion';
import { Player } from '@remotion/player';
import { Slider } from '@/components/ui/slider';
import {
  SceneTransitionWrapper,
  TransitionDipOverlays,
} from '@/components/remotion/SceneTransitions';
import { buildSceneLayout, type SceneLike } from '@/lib/transitions/buildSceneLayout';
import { resolveCameraMoveCrop } from '@/lib/transitions/resolveCameraMoveCrop';
import type { SceneTransition, TransitionDirection, TransitionKind } from '@/lib/transitions/types';

/**
 * Dev-only visual QA page for the scene-transitions feature. Exercises the
 * REAL machinery — buildSceneLayout, SceneTransitionWrapper,
 * TransitionDipOverlays, resolveCameraMoveCrop — the same code paths the
 * editor preview and export renderer use. No auth guard: synthetic content
 * only, nothing user- or project-scoped is fetched.
 */

const FPS = 30;

// Two synthetic 2s scenes — enough runway for a transition window to sit
// fully inside either side, at any duration up to the 1.5s cap.
const DEMO_SCENES: SceneLike[] = [
  { startTime: 0, endTime: 2 },
  { startTime: 2, endTime: 4 },
];

// ----- Synthetic scene content -----

const SceneA: React.FC = () => {
  const frame = useCurrentFrame();
  const t = Math.min(1, frame / (2 * FPS));
  const cx = 10 + t * 80;
  return (
    <AbsoluteFill style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #000000 100%)' }}>
      <AbsoluteFill style={{ alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: 200, fontWeight: 900, color: 'rgba(255,255,255,0.9)', fontFamily: 'sans-serif' }}>
          A
        </span>
      </AbsoluteFill>
      <div
        style={{
          position: 'absolute',
          top: '22%',
          left: `${cx}%`,
          width: 28,
          height: 28,
          borderRadius: '50%',
          background: '#22d3ee',
          transform: 'translate(-50%, -50%)',
          boxShadow: '0 0 12px 4px rgba(34,211,238,0.6)',
        }}
      />
    </AbsoluteFill>
  );
};

const SceneB: React.FC = () => {
  const frame = useCurrentFrame();
  const t = Math.min(1, frame / (2 * FPS));
  const cy = 15 + t * 70;
  return (
    <AbsoluteFill style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #7f1d1d 100%)' }}>
      <AbsoluteFill style={{ alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: 200, fontWeight: 900, color: 'rgba(255,255,255,0.9)', fontFamily: 'sans-serif' }}>
          B
        </span>
      </AbsoluteFill>
      <div
        style={{
          position: 'absolute',
          top: `${cy}%`,
          left: '78%',
          width: 28,
          height: 28,
          background: '#fde047',
          transform: 'translate(-50%, -50%) rotate(45deg)',
          boxShadow: '0 0 12px 4px rgba(253,224,71,0.6)',
        }}
      />
    </AbsoluteFill>
  );
};

// ----- Overlap / transform kinds composition (2-scene cut, real cut window) -----

interface TransitionDemoProps {
  kind: TransitionKind;
  direction?: TransitionDirection;
  durationSec: number;
}

// Typed `any` to match the Player's loose inputProps typing (see
// AnimationPreview in BrandKitSubtitlePreview.tsx) — the actual props are
// still strongly typed via TransitionDemoProps below.
const TransitionDemoComposition: React.FC<any> = ({ kind, direction, durationSec }: TransitionDemoProps) => {
  const layout = useMemo(() => {
    const transition: SceneTransition = { afterSceneIndex: 0, kind, durationSec, direction };
    return buildSceneLayout({ scenes: DEMO_SCENES, transitions: [transition], fps: FPS });
  }, [kind, direction, durationSec]);

  return (
    <AbsoluteFill style={{ background: '#000' }}>
      {layout.blocks.map((block, index) => {
        if (block.durationInFrames <= 0) return null;
        return (
          <Sequence key={index} from={block.from} durationInFrames={block.durationInFrames}>
            <SceneTransitionWrapper cuts={layout.cuts} sceneIndex={index} blockFrom={block.from}>
              {index === 0 ? <SceneA /> : <SceneB />}
            </SceneTransitionWrapper>
          </Sequence>
        );
      })}
      <TransitionDipOverlays cuts={layout.cuts} />
    </AbsoluteFill>
  );
};

// ----- Camera-move composition: continuous background, crop tween -----

const CAMERA_BEFORE = { x: 20, y: 30, zoom: 1 };
const CAMERA_AFTER = { x: 75, y: 65, zoom: 1.6 };

const CameraMoveComposition: React.FC<any> = ({ durationSec }: { durationSec: number }) => {
  const frame = useCurrentFrame();

  const layout = useMemo(() => {
    const transition: SceneTransition = { afterSceneIndex: 0, kind: 'camera-move', durationSec };
    return buildSceneLayout({ scenes: DEMO_SCENES, transitions: [transition], fps: FPS });
  }, [durationSec]);

  const cut = layout.cuts[0] ?? null;
  // The "step" fallback: the crop that would show with no tween at all —
  // a hard jump at the boundary. Mirrors ProjectPreview's
  // resolveCropAtFrame, simplified to two fixed states either side of the cut.
  const resolveAt = (f: number) => (cut && f < cut.boundaryFrame ? CAMERA_BEFORE : CAMERA_AFTER);
  const crop = cut
    ? (resolveCameraMoveCrop({ frame, cuts: layout.cuts, resolveAt }) ?? resolveAt(frame))
    : CAMERA_BEFORE;

  const tiles = Array.from({ length: 8 * 14 }, (_, i) => i);

  return (
    <AbsoluteFill style={{ background: '#0f172a', overflow: 'hidden' }}>
      <div
        style={{
          position: 'absolute',
          inset: '-75%',
          width: '250%',
          height: '250%',
          display: 'grid',
          gridTemplateColumns: 'repeat(14, 1fr)',
          gridTemplateRows: 'repeat(8, 1fr)',
          transformOrigin: '50% 50%',
          transform: `scale(${crop.zoom}) translate(${(50 - crop.x) * 0.5}%, ${(50 - crop.y) * 0.5}%)`,
        }}
      >
        {tiles.map((i) => (
          <div
            key={i}
            style={{
              border: '1px solid #334155',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#64748b',
              fontSize: 10,
              fontFamily: 'monospace',
            }}
          >
            {i}
          </div>
        ))}
      </div>
      <div
        style={{
          position: 'absolute',
          bottom: 8,
          left: 8,
          color: '#fff',
          fontSize: 10,
          fontFamily: 'monospace',
          background: 'rgba(0,0,0,0.6)',
          padding: '2px 6px',
          borderRadius: 4,
        }}
      >
        x={crop.x.toFixed(0)} y={crop.y.toFixed(0)} zoom={crop.zoom.toFixed(2)}
      </div>
    </AbsoluteFill>
  );
};

// ----- Grid cell -----

const CELL_WIDTH = 180;

const TransitionCell: React.FC<{
  label: string;
  kind: TransitionKind;
  direction?: TransitionDirection;
  durationSec: number;
}> = ({ label, kind, direction, durationSec }) => {
  const totalDurationInFrames = useMemo(() => {
    const transition: SceneTransition = { afterSceneIndex: 0, kind, durationSec, direction };
    return Math.max(1, buildSceneLayout({ scenes: DEMO_SCENES, transitions: [transition], fps: FPS }).totalDurationInFrames);
  }, [kind, direction, durationSec]);

  return (
    <div className="flex flex-col items-center gap-1.5">
      <div
        className="rounded-md overflow-hidden border border-border/40 bg-black"
        style={{ width: CELL_WIDTH, aspectRatio: '9 / 16' }}
      >
        <Player
          component={TransitionDemoComposition}
          inputProps={{ kind, direction, durationSec }}
          durationInFrames={totalDurationInFrames}
          compositionWidth={360}
          compositionHeight={640}
          fps={FPS}
          loop
          autoPlay
          initiallyMuted
          controls={false}
          style={{ width: '100%', height: '100%' }}
        />
      </div>
      <span className="text-xs text-muted-foreground text-center">{label}</span>
    </div>
  );
};

const CameraMoveCell: React.FC<{ durationSec: number }> = ({ durationSec }) => {
  const totalDurationInFrames = useMemo(() => {
    const transition: SceneTransition = { afterSceneIndex: 0, kind: 'camera-move', durationSec };
    return Math.max(1, buildSceneLayout({ scenes: DEMO_SCENES, transitions: [transition], fps: FPS }).totalDurationInFrames);
  }, [durationSec]);

  return (
    <div className="flex flex-col items-center gap-1.5">
      <div
        className="rounded-md overflow-hidden border border-border/40 bg-black"
        style={{ width: CELL_WIDTH, aspectRatio: '9 / 16' }}
      >
        <Player
          component={CameraMoveComposition}
          inputProps={{ durationSec }}
          durationInFrames={totalDurationInFrames}
          compositionWidth={360}
          compositionHeight={640}
          fps={FPS}
          loop
          autoPlay
          initiallyMuted
          controls={false}
          style={{ width: '100%', height: '100%' }}
        />
      </div>
      <span className="text-xs text-muted-foreground text-center">camera-move (crop tween)</span>
    </div>
  );
};

// ----- Grid definitions -----

interface CellSpec {
  kind: TransitionKind;
  direction?: TransitionDirection;
  label: string;
}

const OVERLAP_KINDS_ROW: CellSpec[] = [
  { kind: 'crossfade', label: 'crossfade' },
  { kind: 'slide', direction: 'left', label: 'slide (left)' },
  { kind: 'slide', direction: 'right', label: 'slide (right)' },
  { kind: 'slide', direction: 'up', label: 'slide (up)' },
  { kind: 'slide', direction: 'down', label: 'slide (down)' },
  { kind: 'wipe', direction: 'left', label: 'wipe (left)' },
  { kind: 'wipe', direction: 'right', label: 'wipe (right)' },
  { kind: 'wipe', direction: 'up', label: 'wipe (up)' },
  { kind: 'wipe', direction: 'down', label: 'wipe (down)' },
];

const TRANSFORM_KINDS_ROW: CellSpec[] = [
  { kind: 'zoom-in', label: 'zoom-in' },
  { kind: 'zoom-out', label: 'zoom-out' },
  { kind: 'whip-pan', direction: 'left', label: 'whip-pan (left)' },
  { kind: 'whip-pan', direction: 'right', label: 'whip-pan (right)' },
  { kind: 'whip-pan', direction: 'up', label: 'whip-pan (up)' },
  { kind: 'whip-pan', direction: 'down', label: 'whip-pan (down)' },
  { kind: 'blur', label: 'blur' },
  { kind: 'dip-to-black', label: 'dip-to-black' },
  { kind: 'dip-to-white', label: 'dip-to-white' },
];

const TransitionsTestPage = () => {
  const [durationSec, setDurationSec] = useState(0.5);

  return (
    <div className="min-h-screen bg-background text-foreground p-6">
      <div className="max-w-[1400px] mx-auto space-y-8">
        <div>
          <h1 className="text-xl font-bold" data-testid="transitions-test-heading">
            Scene Transitions — Visual QA
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every preset, looping, driven by buildSceneLayout / SceneTransitionWrapper /
            TransitionDipOverlays / resolveTransitionStyles / resolveCameraMoveCrop — the same
            code paths the editor preview and export renderer use. Dev-only page, no auth guard.
          </p>
        </div>

        <div className="flex items-center gap-4 max-w-md">
          <label htmlFor="duration-slider" className="text-sm font-medium whitespace-nowrap">
            Duration: {durationSec.toFixed(2)}s
          </label>
          <Slider
            id="duration-slider"
            min={0.1}
            max={1.5}
            step={0.05}
            value={[durationSec]}
            onValueChange={(v) => setDurationSec(v[0])}
          />
        </div>

        <section className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Overlap kinds
          </h2>
          <div className="flex flex-wrap gap-4">
            {OVERLAP_KINDS_ROW.map((cell) => (
              <TransitionCell
                key={cell.label}
                label={cell.label}
                kind={cell.kind}
                direction={cell.direction}
                durationSec={durationSec}
              />
            ))}
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Transform kinds
          </h2>
          <p className="text-xs text-muted-foreground">
            Keyframe-boundary transitions (clip.data.keyframeTransitions) reuse the exact same
            transform-kind styling via resolveTransitionStyles — no separate section needed here.
          </p>
          <div className="flex flex-wrap gap-4">
            {TRANSFORM_KINDS_ROW.map((cell) => (
              <TransitionCell
                key={cell.label}
                label={cell.label}
                kind={cell.kind}
                direction={cell.direction}
                durationSec={durationSec}
              />
            ))}
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Camera-move
          </h2>
          <p className="text-xs text-muted-foreground">
            Not a per-scene style — resolveCameraMoveCrop tweens a fake crop (x:20,y:30,zoom:1 →
            x:75,y:65,zoom:1.6) across the cut window; outside the window the step values apply
            directly, so the hard cut vs. the smooth tween are both visible in one loop.
          </p>
          <div className="flex flex-wrap gap-4">
            <CameraMoveCell durationSec={durationSec} />
          </div>
        </section>
      </div>
    </div>
  );
};

export default TransitionsTestPage;
